import { useMemo } from 'react'
import Highlighter from 'react-highlight-words'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import { DateWithToolTip, Flex, CopytoClipBoardButton, Tooltip, Icon } from 'components'
import Box from 'components/Box'
import Dot from 'components/Dot'
import Text from 'components/Text'

import { colors } from 'utils/colors'
import { SearchData } from 'services/types/search'

import SearchResultsRowItem from './SearchResultsRowItem'

import LeadStatusChip, { StatusGroup } from 'modules/Leads/shared/LeadStatus/LeadStatus'
import { getLeadRoute } from 'utils/url'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'

type SearchResultsRowProps = {
  rowIndex: number
  searchTerm: string
  data: SearchData
}

const SearchResultsRow = ({ rowIndex, data, searchTerm }: SearchResultsRowProps) => {
  const {
    lead_id,
    account_name,
    match,
    lead_owner,
    lead_in_date,
    lead_status_group_name,
    last_activity,
    can_access,
  } = data
  const { t } = useTranslation()
  const { openInReusableTab } = useReusableTab()

  const items = useMemo(() => match.filter((i) => i.type !== 'account_name'), [match])

  const openLeadLink = getLeadRoute({ leadId: lead_id })
  const clipboardLink = `${window.location.origin}${openLeadLink}`

  const handleOnClick = () => {
    openInReusableTab(openLeadLink)
  }

  const getLastActivity = () => (
    <>
      {last_activity && (
        <Flex gap="0.5rem">
          <Text fontSize={13} color="gray5">
            {t('last_activity_time', { time: '' })}
          </Text>
          <DateWithToolTip fontSize={13} color="gray5" date={last_activity} dateFormat="PPp" showHumanized />
        </Flex>
      )}
      {lead_in_date && (
        <>
          {last_activity && (
            <Text fontSize={13} color="gray5" inline>
              |
            </Text>
          )}
          <Flex gap="0.5rem">
            <Text fontSize={13} color="gray5">
              {t('lead_in_date_time', { time: '' })}
            </Text>
            <DateWithToolTip fontSize={13} color="gray5" date={lead_in_date} dateFormat="PPp" showHumanized />
          </Flex>
        </>
      )}
    </>
  )

  const generateTitle = () => {
    const content = account_name ? (
      <Text fontSize={14} fontWeight="medium" variant={can_access ? 'link' : 'default'} inline>
        <Highlighter
          searchWords={[searchTerm]}
          autoEscape={true}
          textToHighlight={account_name}
          highlightClassName="word-highlight"
        />
      </Text>
    ) : (
      <Text fontSize={14} fontWeight="medium" inline>
        -
      </Text>
    )
    if (!can_access) {
      return (
        <Tooltip title="lead_search_no_permission" position="bottom">
          {content}
        </Tooltip>
      )
    }
    return content
  }

  return (
    <>
      <Container canAccess={can_access} onClick={can_access ? () => handleOnClick() : undefined}>
        <Flex gap="0.5rem" className="lead-name">
          {!can_access && <Icon icon={['far', 'lock-keyhole']} fontSize={14} color="gray5" />}
          {generateTitle()}
          <Tooltip title="Copy link to clipboard" position="right">
            <CopytoClipBoardButton
              icon={['far', 'clone']}
              size={14}
              iconColor="gray5"
              clipBoardContent={clipboardLink}
            />
          </Tooltip>
        </Flex>
        <ActivityWrapper>
          <LeadStatusChip statusGroup={lead_status_group_name.toLowerCase() as StatusGroup} />
          {lead_owner?.trim() && (
            <>
              <Text color="gray5" whiteSpace="nowrap" fontSize={13}>
                lead_owner_lbl
              </Text>
              <Text color="gray5" whiteSpace="nowrap" fontSize={13}>
                {lead_owner?.trim()}
              </Text>
              <Dot />
            </>
          )}
          {getLastActivity()}
        </ActivityWrapper>

        <Box mt="6px" display="flex" flexWrap="wrap">
          {items.map((field, index) => (
            <SearchResultsRowItem
              key={`search-result-row-${rowIndex}-item-${index}`}
              type={field.type}
              value={field.is_active ? field.value : `${field.value} (${t('deactivated_lbl')})`}
              searchTerm={searchTerm}
            />
          ))}
        </Box>
      </Container>
    </>
  )
}

const Container = styled.div<{ canAccess: boolean }>`
  padding: 8px 10px 2px 16px;
  border-bottom: 1px solid;
  border-bottom-color: ${colors.gray3};
  background-color: ${({ canAccess }) => (canAccess ? colors.white : colors.gray1)};
  cursor: ${({ canAccess }) => (canAccess ? 'pointer' : 'arrow')};

  :hover {
    background-color: ${colors.gray1};
  }

  .lead-name div {
    text-decoration: none;
  }

  .word-highlight {
    background-color: ${({ canAccess }) => (canAccess ? colors.white : colors.gray1)};
    color: ${colors.green2};

    &:hover {
      color: ${({ canAccess }) => (canAccess ? colors.blue2 : colors.gray5)};
    }
  }
`

const ActivityWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  row-gap: 0.1rem;
  line-height: 16px;
`

export default SearchResultsRow
